import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPlayback } from '../services/api';
import { PlaybackResponse } from '../types/api';

declare global {
  interface Window {
    Stream?: (iframe: HTMLIFrameElement) => {
      addEventListener: (eventName: string, listener: () => void) => void;
      removeEventListener?: (eventName: string, listener: () => void) => void;
      play: () => Promise<void>;
      muted: boolean;
    };
  }
}

const STREAM_SDK_URL = 'https://embed.cloudflarestream.com/embed/sdk.latest.js';

export const PlaybackPage: React.FC = () => {
  const { id = '' } = useParams();
  const [playback, setPlayback] = useState<PlaybackResponse | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const response = await getPlayback(id);
        if (active) {
          setPlayback(response);
          setError('');
        }
      } catch {
        if (active) setError('Playback is unavailable. Please confirm your rental is active.');
      }
    };

    refresh();
    const timer = window.setInterval(refresh, 150000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [id]);

  useEffect(() => {
    if (!playback?.playbackUrl) return;

    let disposed = false;
    let onPlay: (() => void) | null = null;
    let player: ReturnType<NonNullable<typeof window.Stream>> | null = null;

    const setupPlayer = () => {
      const iframe = document.getElementById('stream-player');
      if (disposed || !(iframe instanceof HTMLIFrameElement) || typeof window.Stream !== 'function') return;

      player = window.Stream(iframe);
      onPlay = () => {
        // Keep this for troubleshooting and basic telemetry.
        console.log('playing!');
      };

      player.addEventListener('play', onPlay);
      player.play().catch(() => {
        if (disposed) return;
        console.log('playback failed, muting to try again');
        player!.muted = true;
        player!.play().catch(() => undefined);
      });
    };

    const existingScript = document.querySelector(`script[src="${STREAM_SDK_URL}"]`) as HTMLScriptElement | null;
    if (window.Stream) {
      setupPlayer();
    } else if (existingScript) {
      existingScript.addEventListener('load', setupPlayer);
    } else {
      const script = document.createElement('script');
      script.src = STREAM_SDK_URL;
      script.async = true;
      script.addEventListener('load', setupPlayer);
      document.body.appendChild(script);
    }

    return () => {
      disposed = true;
      if (onPlay && player?.removeEventListener) {
        player.removeEventListener('play', onPlay);
      }
      const script = document.querySelector(`script[src="${STREAM_SDK_URL}"]`) as HTMLScriptElement | null;
      if (script) {
        script.removeEventListener('load', setupPlayer);
      }
    };
  }, [playback?.playbackUrl]);

  return (
    <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-4">
        <div>
          <h1 className="text-2xl font-bold text-white">{playback?.movieTitle || 'Playback'}</h1>
          {playback && <p className="text-sm text-stone-300">Rental expires {new Date(playback.rentalExpiresAt).toLocaleString()}</p>}
        </div>
      </div>
      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-700">{error}</div>
      ) : (
        <div className="overflow-hidden rounded-lg bg-black">
          {playback ? (
            <iframe
                id="stream-player"
                title="Viu movie player"
                src={playback.playbackUrl}
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                allowFullScreen
                loading="lazy"
                className="aspect-video w-full"
                height="720"
                width="1280"
            />
          ) : (
            <div className="grid aspect-video place-items-center text-white" style={{"width":"1280px","height":"720px"}}>Preparing secure playback...</div>
          )}
        </div>
      )}
      {/*{playback && <p className="mt-3 text-sm text-stone-600">Playback token refreshes automatically every 2.5 minutes.</p>}*/}
    </main>
  );
};
